import React from 'react';
import { Stack, FormControlLabel, Switch, Typography, Tooltip, Divider, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TableVisibilitySettings } from '../../types/app';

interface VisiblePropertiesControlsProps {
  viewSettings: TableVisibilitySettings;
  onViewSettingsChange: (settings: Partial<TableVisibilitySettings>) => void;
  disabledProperties?: Partial<Record<'frequency' | 'column_percent' | 'row_percent' | 'significance', boolean>>;
  hiddenProperties?: Partial<Record<'frequency' | 'column_percent' | 'row_percent' | 'significance', boolean>>;
  // Cache control props (for managers only)
  showCacheSwitch?: boolean;
  useCache?: boolean;
  onCacheToggle?: (useCache: boolean) => void;
  // Significance data availability
  hasSigData?: boolean;
}

export const VisiblePropertiesControls: React.FC<VisiblePropertiesControlsProps> = ({
  viewSettings,
  onViewSettingsChange,
  disabledProperties,
  hiddenProperties,
  showCacheSwitch = false,
  useCache = true,
  onCacheToggle,
  hasSigData,
}) => {
  // Create handler that updates the full settings object
  const handleToggle = (key: 'frequency' | 'column_percent' | 'row_percent' | 'significance') => {
    onViewSettingsChange({ [key]: !viewSettings[key] })
  }

  const switchStyles = {
    frequency: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#000000',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#000000',
      },
      '& .MuiSwitch-root': {
        marginRight: 0,
      },
    },
    columnPercent: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#0066cc',
        '&:hover': {
          backgroundColor: 'rgba(0, 102, 204, 0.08)',
        },
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#0066cc',
      },
      '& .MuiSwitch-root': {
        marginRight: 0,
      },
    },
    rowPercent: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#cc0000',
        '&:hover': {
          backgroundColor: 'rgba(204, 0, 0, 0.08)',
        },
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#cc0000',
      },
      '& .MuiSwitch-root': {
        marginRight: 0,
      },
    },
    significance: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#ff9800',
        '&:hover': {
          backgroundColor: 'rgba(255, 152, 0, 0.08)',
        },
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#ff9800',
      },
      '& .MuiSwitch-root': {
        marginRight: 0,
      },
    },
    cache: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#4caf50',
        '&:hover': {
          backgroundColor: 'rgba(76, 175, 80, 0.08)',
        },
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#4caf50',
      },
      '& .MuiSwitch-root': {
        marginRight: 0,
      },
    },
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {!hiddenProperties?.frequency && (
        <FormControlLabel
          control={
            <Switch
              checked={viewSettings.frequency ? true : false}
              onChange={() => handleToggle('frequency')}
              size="small"
              sx={switchStyles.frequency}
            />
          }
          label={<Typography variant="caption">Freq</Typography>}
          sx={{ m: 0, mr: 0.5 }}
        />
      )}
      {!hiddenProperties?.column_percent && (
        <FormControlLabel
          control={
            <Switch
              checked={viewSettings.column_percent ? true : false}
              onChange={() => handleToggle('column_percent')}
              size="small"
              sx={switchStyles.columnPercent}
            />
          }
          label={<Typography variant="caption">Col%</Typography>}
          sx={{ m: 0, mr: 0.5 }}
        />
      )}
      {!hiddenProperties?.row_percent && (
        <FormControlLabel
          control={
            <Switch
              checked={viewSettings.row_percent ? true : false}
              onChange={() => handleToggle('row_percent')}
              size="small"
              sx={switchStyles.rowPercent}
            />
          }
          label={<Typography variant="caption">Row%</Typography>}
          sx={{ m: 0, mr: 0.5 }}
        />
      )}
      {!hiddenProperties?.significance && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={viewSettings.significance ? true : false}
                onChange={() => handleToggle('significance')}
                disabled={disabledProperties?.significance}
                size="small"
                sx={switchStyles.significance}
              />
            }
            label={<Typography variant="caption">Sig</Typography>}
            sx={{ m: 0, mr: 0 }}
          />
          {viewSettings.significance && hasSigData === false && (
            <Tooltip title="Significance is enabled but this table has no significance data. Enable sig testing in the spec settings to generate significance data.">
              <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main', ml: 0.25 }} />
            </Tooltip>
          )}
        </Box>
      )}
      {showCacheSwitch && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title={useCache ? "Cache enabled - toggle to bypass cache" : "Cache bypassed - data loaded fresh from server"}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCache}
                  onChange={() => onCacheToggle?.(!useCache)}
                  size="small"
                  sx={switchStyles.cache}
                />
              }
              label={<Typography variant="caption">Cache</Typography>}
              sx={{ m: 0, mr: 0.5 }}
            />
          </Tooltip>
        </>
      )}
    </Stack>
  )
}

export default VisiblePropertiesControls
